import React from "react";
import { Box, Text, Button, Tip, Layer } from "grommet";
import { useLocation, useNavigate } from "react-router-dom";
import PageMainCanvas from "../mainpage/PageMainCanvas";
import { HiOutlineBuildingOffice } from "react-icons/hi2";
import useInst from "../../hooks/useInst";
import useBook from "../../hooks/useBook";
import usePsnorder from "../../hooks/usePsnorder";
import { CaretDownFill, CaretRightFill, LinkNext } from "grommet-icons";
import { BsChevronLeft, BsChevronRight, BsChevronUp } from "react-icons/bs";
import sortArray from "../../functions/sortArray";
import PageRedbookAnalysisTable from "./PageRedbookAnalysisTable";
import {
    colorPaletteR,
    selectedInstsAnalysisR,
    selectedInstsNameAnalysisR,
} from "../../recoils/selectedInsts";
import { useRecoilState } from "recoil";
import getColors from "../../functions/getColors";

interface PersonPsnorder {
    person_id_new: string;
    person_id: number;
    person_name: string;
    main_start_year?: number;
    main_end_year?: number;
    start_year?: number;
    end_year?: number;
    psnorders: PersonYearPsnorder[];
}
interface PersonYearPsnorder {
    person_id: number;
    person_name: string;
    year: number;
    person_year_data: PrYrItPsnorder[];
}
interface PrYrItPsnorder {
    person_id: number;
    person_name: string;
    year: number;
    inst_id: number | null;
    inst_name: string;
    inst_group: string;
    psnorder: any;
}
interface InstNameBase {
    inst_category?: string;
    inst_id?: number;
    inst_type?: string;
    primaryname: string;
}
interface PsnorderBase {
    book_id: number;
    toc_id: number;
    inst_id?: number | null;
    instname?: string;
    person_id: number;
    personname: string;
    po_date: string;
    po_type: string;
    po_year: number;
    psnorder_id: number;
    record_text: string;
    status_grade?: string;
    status_memo?: string;
    status_merit?: string;
    status_pos?: string;
    status_text?: string;
}

const PageRedbookAnalysis = () => {
    // reading address
    let location = useLocation();
    let navigate = useNavigate();

    const id_list = location.hash.split("#").pop() || "";
    const ids = id_list.split("&");

    const [redbooks, setRedbooks] = React.useState<any[]>([]);

    const [mainInstsInfo, setMainInstsInfo] = React.useState<string[]>([]);
    const [readyMainInstsInfo, setReadyMainInstsInfo] = React.useState(false);

    const [psnorders, setPsnorders] = React.useState<any[]>([]);
    const [mainPsnordersIds, setMainPsnordersIds] = React.useState<any[]>([]);
    const [readyPsnorders, setReadyPsnorders] = React.useState(false);

    const [personPsnorders, setPersonPsnorders] = React.useState<any[]>([]);
    const [readyPersons, setReadyPersons] = React.useState(false);

    const [otherPsnorders, setOtherPsnorders] = React.useState<any[]>([]);

    const [relatedInstsInfo, setRelatedInstsInfo] = React.useState<any[]>([]);
    const [relatedInstsIds, setRelatedInstsIds] = React.useState<any[]>([]);
    const [readyRelatedInstsInfo, setReadyRelatedInstsInfo] =
        React.useState(false);

    const [otherInstsInfo, setOtherInstsInfo] = React.useState<any[]>([]);
    const [readyOtherInstsInfo, setReadyOtherInstsInfo] = React.useState(false);

    const [colorPalette, setColorPalette] = useRecoilState(colorPaletteR);

    const getInsts = async (inst_ids: any) => {
        let res = await useInst.getInstsByIds(inst_ids);
        return res.insts;
    };
    const getRedbooks = async () => {
        let res = await useBook.getBookList("redbook");
        return res.books;
    };

    // step 1 - institution infos
    React.useEffect(() => {
        setReadyMainInstsInfo(false);
        setReadyOtherInstsInfo(false);
        setReadyPsnorders(false);
        setReadyPersons(false);
        setReadyRelatedInstsInfo(false);
        setReadyOtherInstsInfo(false);
        setResultSorted(false);
        setMainInstsInfo([]);
        setPsnorders([]);
        setMainPsnordersIds([]);
        setPersonPsnorders([]);
        setOtherPsnorders([]);
        setRelatedInstsInfo([]);
        setRelatedInstsIds([]);
        setOtherInstsInfo([]);
        setSortedPersonPsnorders([]);
        setSelectedInsts([]);
        setSelectedInstsName([]);

        const receiveMainInstInfo = async () => {
            let res_insts = await getInsts(ids);
            //console.log("MainInstInfos", res_insts);
            setMainInstsInfo(res_insts);
            setReadyMainInstsInfo(true);

            let res_books = await getRedbooks();
            // console.log("Redbooks", res_books)
            setRedbooks(res_books);
        };
        receiveMainInstInfo();
        let new_color_palette = getColors([]);
        setColorPalette(new_color_palette);
    }, [location]);

    // step 2 - call psnorders of main insts
    React.useEffect(() => {
        if (readyMainInstsInfo) {
            setReadyPsnorders(false);
            const receivePsnorders = async () => {
                let res_psnorders = await useInst.getPsnordersByInstIds(ids);
                // console.log("Psnorders", res_psnorders)
                setPsnorders(res_psnorders);
                setReadyPsnorders(true);
            };
            receivePsnorders();
        }
    }, [readyMainInstsInfo]);

    // step 3 - extract person_ids from psnorders
    React.useEffect(() => {
        if (readyPsnorders) {
            setReadyPersons(false);
            const receivePersons = async () => {
                let P_psnorder_list: any = [];
                let PY_psnorder_list: any = [];
                let PYI_psnorder_list: any = [];
                let main_psnorder_ids: any = [];

                const n_psnorders = psnorders.length;
                let n_count = 0;

                const newPrYrItPsnorder = (psnorder: any) => {
                    let PYI_psnorder: PrYrItPsnorder = {
                        person_id: psnorder.person_id,
                        person_name: psnorder.personname,
                        year: psnorder.po_year,
                        inst_id: psnorder.inst_id,
                        inst_name: psnorder.instname,
                        inst_group: "main",
                        psnorder: psnorder,
                    };
                    PYI_psnorder_list.push(PYI_psnorder);
                    return PYI_psnorder;
                };
                const newPersonYearPsnorder = (psnorder: any) => {
                    let PYI_psnorder: PrYrItPsnorder =
                        newPrYrItPsnorder(psnorder);
                    let PY_psnorder: PersonYearPsnorder = {
                        person_id: psnorder.person_id,
                        person_name: psnorder.personname,
                        year: psnorder.po_year,
                        person_year_data: [PYI_psnorder],
                    };
                    PY_psnorder_list.push(PY_psnorder);
                    return PY_psnorder;
                };
                const newPersonPsnorder = (psnorder: any) => {
                    let PY_psnorder: PersonYearPsnorder =
                        newPersonYearPsnorder(psnorder);
                    let P_psnorder: PersonPsnorder = {
                        person_id_new: psnorder.person_id.toString(),
                        person_id: psnorder.person_id,
                        person_name: psnorder.personname,
                        main_start_year: psnorder.po_year,
                        main_end_year: psnorder.po_year,
                        start_year: psnorder.po_year,
                        end_year: psnorder.po_year,
                        psnorders: [PY_psnorder],
                    };
                    P_psnorder_list.push(P_psnorder);
                    return P_psnorder;
                };
                psnorders.forEach((x: any) => {
                    n_count += 1;
                    main_psnorder_ids.push(x.psnorder.psnorder_id);
                    // find already exist person_id
                    let prsn_index = P_psnorder_list.findIndex(
                        (p: any) => p.person_id === x.psnorder.person_id
                    );
                    let psnorder_with_inst = x.psnorder;
                    psnorder_with_inst.inst_id = x.inst.inst_id;
                    psnorder_with_inst.instname = x.inst.primaryname;

                    if (prsn_index < 0) {
                        // console.log("New person from psnorders", x)
                        let P_psnorder: PersonPsnorder =
                            newPersonPsnorder(psnorder_with_inst);
                    } else {
                        let a_P_psnorder = P_psnorder_list[prsn_index];
                        // console.log("Existing person from psnorders", x)
                        // check if person_year exists
                        let person_year_index = PY_psnorder_list.findIndex(
                            (p: any) =>
                                p.person_id === x.psnorder.person_id &&
                                p.year === x.psnorder.po_year
                        );
                        if (person_year_index < 0) {
                            // console.log("New person_year from psnorders", x)
                            let PY_psnorder: PersonYearPsnorder =
                                newPersonYearPsnorder(psnorder_with_inst);
                            a_P_psnorder.psnorders.push(PY_psnorder);
                        } else {
                            let PYI_psnorder: PrYrItPsnorder =
                                newPrYrItPsnorder(psnorder_with_inst);
                            let a_PY_psnorder =
                                PY_psnorder_list[person_year_index];
                            a_PY_psnorder.person_year_data.push(PYI_psnorder);
                            a_P_psnorder.psnorders.push(a_PY_psnorder);
                        }
                        if (x.psnorder.po_year < a_P_psnorder.main_start_year) {
                            a_P_psnorder.main_start_year = x.psnorder.po_year;
                            a_P_psnorder.start_year = x.psnorder.po_year;
                        } else if (
                            x.psnorder.po_year > a_P_psnorder.main_end_year
                        ) {
                            a_P_psnorder.main_end_year = x.psnorder.po_year;
                            a_P_psnorder.end_year = x.psnorder.po_year;
                        }
                    }
                    if (n_count === n_psnorders) {
                        setPersonPsnorders(P_psnorder_list);
                        setReadyPersons(true);
                        setMainPsnordersIds(main_psnorder_ids);
                    }
                });
                // console.log("PrYrItPsnorders", PYI_psnorder_list)
                // console.log("PersonYearPsnorders", PY_psnorder_list)
                // console.log("PersonPsnorders", P_psnorder_list)
            };
            receivePersons();
        }
    }, [readyPsnorders]);

    // step 4 - psnorders from api [16] POST:/psnorders/relation
    React.useEffect(() => {
        if (readyPersons) {
            const receivePsnordersFromPersonIds = async () => {
                const n_persons = personPsnorders.length;
                let n_count = 0;
                let insts_info: any = [];
                let personIds = personPsnorders.map((p: any) => p.person_id);
                let res_psnorders = await usePsnorder.getPsnordersBySPR({
                    person_ids: personIds,
                });
                // console.log("PsnordersFromPersonIds", res_psnorders)
                setOtherPsnorders(res_psnorders.psnorders);
            };
            receivePsnordersFromPersonIds();
        }
    }, [readyPersons]);

    // step 5 - get data P_psnorder from other psnorders and add to P_psnorder
    React.useEffect(() => {
        if (readyPersons && otherPsnorders.length > 0) {
            setReadyRelatedInstsInfo(false);

            // from mainInstsInfo, call relatedInstsInfo with api [18] getInstsByName
            const receiveRelatedInstsInfo = async () => {
                let inst_names = mainInstsInfo.map(
                    (p: any) => p.inst.primaryname
                );
                let inst_names_last = inst_names.map((p: any) =>
                    p.split("/").pop()
                );
                // duplicate removal inst_names_last
                inst_names_last = inst_names_last.filter(
                    (item: any, index: any) =>
                        inst_names_last.indexOf(item) === index
                );

                let related_insts_info: any = [];
                const n_insts = inst_names_last.length;
                let n_count = 0;
                // console.log("receiveRelatedInstsInfo start", mainInstsInfo, inst_names_last)
                const get_insts_by_name = async (inst_name: string) => {
                    let res_insts = await useInst.getInstsByName(inst_name);
                    return res_insts;
                };
                inst_names_last.forEach((x: any) => {
                    n_count += 1;
                    get_insts_by_name(x).then((res_insts: any) => {
                        // console.log("get insts by name", res_insts)
                        let res_inst_info_list = res_insts.insts;
                        // console.log("res_inst_info_list", res_inst_info_list)
                        related_insts_info.push(...res_inst_info_list);
                        if (n_count === n_insts) {
                            // remove related_insts_info with mainInstsInfo
                            let ids = mainInstsInfo.map(
                                (p: any) => p.inst.inst_id
                            );
                            // console.log("ids", ids)
                            related_insts_info = related_insts_info.filter(
                                (item: any) => !ids.includes(item.inst.inst_id)
                            );
                            // remove duplicate
                            related_insts_info = related_insts_info.filter(
                                (item: any, index: any) =>
                                    related_insts_info.indexOf(item) === index
                            );
                            setRelatedInstsInfo(related_insts_info);
                            setReadyRelatedInstsInfo(true);
                            // console.log("related_insts_info", related_insts_info)
                            setRelatedInstsIds(
                                related_insts_info.map(
                                    (p: any) => p.inst.inst_id
                                )
                            );
                        }
                    });
                });
            };
            // console.log("call receiveRelatedInstsInfo")
            receiveRelatedInstsInfo();
        }
    }, [readyPersons, otherPsnorders]);

    // step 6 - other insts info from otherPsnorders
    React.useEffect(() => {
        if (readyRelatedInstsInfo) {
            setReadyOtherInstsInfo(false);
            let P_psnorder_list = personPsnorders;
            let other_insts_info = otherInstsInfo;
            // console.log("P_psnorder copy", P_psnorder_list)
            let PY_psnorder_list: any = [];
            P_psnorder_list.forEach((p: any) => {
                PY_psnorder_list.push(...p.psnorders);
            });
            let PYI_psnorder_list: any = [];

            PY_psnorder_list.forEach((x: any) => {
                // console.log("check PY_psnorder", x)
                let a_person_year_data = x.person_year_data;
                PYI_psnorder_list.push(...a_person_year_data);
            });

            let related_psnorder_ids: any = [];
            // console.log("PY_psnorder copy", PY_psnorder_list)
            // console.log("PYI_psnorder copy", PYI_psnorder_list)

            const newPrYrItPsnorderWGroup = (props: any) => {
                let psnorder = props.psnorder;
                let inst_group = props.inst_group;
                let PYI_psnorder: PrYrItPsnorder = {
                    person_id: psnorder.person_id,
                    person_name: psnorder.personname,
                    year: psnorder.po_year,
                    inst_id: psnorder.inst_id,
                    inst_name: psnorder.instname,
                    inst_group: inst_group,
                    psnorder: psnorder,
                };
                PYI_psnorder_list.push(PYI_psnorder);
                return PYI_psnorder;
            };
            const newPersonYearPsnorderWGroup = (props: any) => {
                let po = props.psnorder;
                let igroup = props.inst_group;
                let PYI_psnorder: PrYrItPsnorder = newPrYrItPsnorderWGroup({
                    psnorder: po,
                    inst_group: igroup,
                });
                let PY_psnorder: PersonYearPsnorder = {
                    person_id: po.person_id,
                    person_name: po.personname,
                    year: po.po_year,
                    person_year_data: [PYI_psnorder],
                };
                PY_psnorder_list.push(PY_psnorder);
                return PY_psnorder;
            };

            const n_psnorders = otherPsnorders.length;
            let n_count = 0;

            otherPsnorders.forEach((x: any) => {
                n_count += 1;
                //to do : set up other insts info, add P_psnorder from otherpsnorders
                let record_text_div = x.record_text.split("#");
                let psnorder_base: PsnorderBase = {
                    book_id: x.book_id,
                    toc_id: x.toc_id,
                    inst_id: null,
                    instname: x.status_text,
                    person_id: x.person_id,
                    personname: x.personname,
                    po_date: x.po_date,
                    po_type: x.po_type,
                    po_year: x.po_year,
                    psnorder_id: x.psnorder_id,
                    record_text: x.record_text,
                    status_grade: record_text_div[5],
                    status_memo: record_text_div[4],
                    status_merit: record_text_div[6],
                    status_pos: record_text_div[3],
                    status_text: x.status_text,
                };

                // check if mainPsnordersIds includes psnorder_id
                if (
                    !mainPsnordersIds.includes(x.psnorder_id) &&
                    x.po_type === "redbook"
                ) {
                    let inst_info: any = null;
                    let inst_group = "other";

                    // check if inst exists in relatedInstsInfo
                    let related_inst_index = relatedInstsInfo.findIndex(
                        (p: any) => p.inst.primaryname === x.status_text
                    );
                    if (related_inst_index < 0) {
                        // other inst
                        let new_inst_info = {
                            inst: {
                                inst_id: null,
                                primaryname: x.status_text,
                                primaryname_ko: null,
                                inst_type: null,
                                inst_category: null,
                            },
                            names: [],
                        };
                        inst_info = new_inst_info;
                        other_insts_info.push(new_inst_info);
                    } else {
                        // related_inst
                        inst_info = relatedInstsInfo[related_inst_index];
                        inst_group = "related";
                        psnorder_base.inst_id = inst_info.inst.inst_id;
                        psnorder_base.instname = inst_info.inst.primaryname;
                    }

                    // find P_psnorder
                    let P_psnorder_index = P_psnorder_list.findIndex(
                        (p: any) => p.person_id === x.person_id
                    );

                    if (P_psnorder_index < 0) {
                        // console.log("ERROR NEW PERSON NOT ALLOWED", x);
                    } else {
                        let a_P_psnorder = P_psnorder_list[P_psnorder_index];
                        // check if PY_psnorder
                        let PY_psnorder_index = PY_psnorder_list.findIndex(
                            (p: any) =>
                                p.person_id === x.person_id &&
                                p.year === x.po_year
                        );
                        if (PY_psnorder_index < 0) {
                            // new PY_psnorder
                            let new_PY_psnorder: PersonYearPsnorder =
                                newPersonYearPsnorderWGroup({
                                    psnorder: psnorder_base,
                                    inst_group: inst_group,
                                });
                            a_P_psnorder.psnorders.push(new_PY_psnorder);
                            // sort a_P_psnorder.psnorders
                            a_P_psnorder.psnorders.sort(
                                (a: any, b: any) => a.year - b.year
                            );
                            // console.log("a_P_psnorder", a_P_psnorder)
                        } else {
                            let a_PY_psnorder =
                                PY_psnorder_list[PY_psnorder_index];
                            // check if PYI_psnorder
                            let PYI_psnorder_index =
                                PYI_psnorder_list.findIndex(
                                    (p: any) =>
                                        p.person_id === x.person_id &&
                                        p.year === x.po_year &&
                                        p.psnorder.psnorder_id === x.psnorder_id
                                );
                            if (PYI_psnorder_index < 0) {
                                // new PYI_psnorder
                                let new_PYI_psnorder: PrYrItPsnorder =
                                    newPrYrItPsnorderWGroup({
                                        psnorder: psnorder_base,
                                        inst_group: inst_group,
                                    });

                                // modify exist PY_psnorder
                                let PYData = a_PY_psnorder.person_year_data;
                                PYData.push(new_PYI_psnorder);
                                // console.log("PYData", PYData)
                            } else {
                                // existing PYI_psnorder
                                // console.log("ERROR EXISTING PYI_psnorder")
                            }
                        }
                        if (x.po_year < a_P_psnorder.start_year) {
                            a_P_psnorder.start_year = x.po_year;
                        } else if (x.po_year > a_P_psnorder.end_year) {
                            a_P_psnorder.end_year = x.po_year;
                        }
                    }
                }
                if (n_count === n_psnorders) {
                    let new_person_psnorders = changeSortOption({
                        option: "person_name",
                        person_psnorders: P_psnorder_list,
                    });
                    setShowOption("position");
                    setSortOption("person_name");
                    setResultSorted(true);
                    setPersonPsnorders(new_person_psnorders);
                    // sort other_insts_info
                    other_insts_info.sort((a: any, b: any) =>
                        a.inst.primaryname < b.inst.primaryname ? -1 : 1
                    );
                    // remove duplicate
                    let new_other_insts: any = [];
                    other_insts_info.forEach((p: any) => {
                        let index = new_other_insts.findIndex(
                            (q: any) =>
                                q.inst.primaryname === p.inst.primaryname
                        );
                        if (index < 0) {
                            new_other_insts.push(p);
                        }
                    });
                    // console.log("OtherInstsInfo", new_other_insts)
                    setOtherInstsInfo(new_other_insts);
                    setReadyOtherInstsInfo(true);
                    // console.log("PersonPsnorder finalize", new_person_psnorders)
                } else {
                    setResultSorted(false);
                }
            });
        }
    }, [readyRelatedInstsInfo]);

    // step 7 - filtered personPsnorders > personPsnorderTable
    // sort option
    const [sortOption, setSortOption] = React.useState("person_name");
    const [sortDirection, setSortDirection] = React.useState("asc");

    const AInstButton = (props: any) => {
        let name = props.name;
        let name_ko = props.name_ko;
        let id = props.id;
        let pindex = mainInstsInfo.findIndex((p: any) => p.inst.inst_id === id);
        return (
            <Tip
                plain
                content={
                    name_ko !== name && (
                        <Box
                            background="rgba(255,255,255, 0.9)"
                            align="center"
                            justify="center"
                        >
                            <Text size="10px">{name_ko}</Text>
                        </Box>
                    )
                }
            >
                <Button
                    margin={{ horizontal: "5px" }}
                    plain
                    label={
                        <Box
                            margin={{ vertical: "5px" }}
                            hoverIndicator
                            pad="10px"
                            round="5px"
                            direction="row"
                            align="center"
                            justify="center"
                            border={{ color: "black" }}
                            background="rgba(255,255,255,0.9)"
                        >
                            <Text size="12px" margin={{ right: "2px" }}>
                                {pindex + 1}
                            </Text>
                            <Text size="16px">{name}</Text>
                        </Box>
                    }
                />
            </Tip>
        );
    };
    const [showOption, setShowOption] = React.useState("position");
    const [resultSorted, setResultSorted] = React.useState(true);
    const [sortedPersonPsnorders, setSortedPersonPsnorders] = React.useState<
        any[]
    >([]);

    const changeSortOption = (props: any) => {
        let option = props.option;
        let new_person_psnorders = props.person_psnorders;
        if (option) {
            if (option === "person_name") {
                new_person_psnorders.sort((a: any, b: any) =>
                    a.person_name < b.person_name ? -1 : 1
                );
            } else if (option === "main_start_year") {
                new_person_psnorders.sort((a: any, b: any) =>
                    a.main_start_year < b.main_start_year ? -1 : 1
                );
            } else if (option === "start_year") {
                new_person_psnorders.sort((a: any, b: any) =>
                    a.start_year < b.start_year ? -1 : 1
                );
            }
            // console.log("changeSortOption", option, new_person_psnorders);
            setResultSorted(true);
            setSortedPersonPsnorders(new_person_psnorders);
            return new_person_psnorders;
        } else {
            setResultSorted(true);
            setSortedPersonPsnorders(new_person_psnorders);
            return new_person_psnorders;
        }
    };

    const SelectSortOption = () => {
        const SortOptionText = (props: any) => {
            let sort_option = props.sort_option;
            let sort_option_kr = props.sort_option_kr;
            let sort_option_en = props.sort_option_en;
            return (
                <Text
                    size="12px"
                    weight={sort_option === sort_option_en ? "bold" : "normal"}
                >
                    {sort_option_kr}
                </Text>
            );
        };
        return (
            <Box direction="row" align="center" justify="center" margin="5px">
                <Button
                    plain
                    margin={{ horizontal: "10px" }}
                    disabled
                    label={
                        <SortOptionText
                            sort_option={""}
                            sort_option_en={""}
                            sort_option_kr={"정렬"}
                        />
                    }
                />
                <Button
                    plain
                    margin={{ horizontal: "10px" }}
                    label={
                        <SortOptionText
                            sort_option={sortOption}
                            sort_option_en={"person_name"}
                            sort_option_kr={"이름순"}
                        />
                    }
                    onClick={() => {
                        sortOption !== "person_name" &&
                            changeSortOption({
                                option: "person_name",
                                person_psnorders: personPsnorders,
                            });
                        setSortOption("person_name");
                    }}
                    hoverIndicator
                />
                <Button
                    plain
                    margin={{ horizontal: "10px" }}
                    label={
                        <SortOptionText
                            sort_option={sortOption}
                            sort_option_en={"main_start_year"}
                            sort_option_kr={"선택기관 근무연도 시작순"}
                        />
                    }
                    onClick={() => {
                        sortOption !== "main_start_year" &&
                            changeSortOption({
                                option: "main_start_year",
                                person_psnorders: personPsnorders,
                            });
                        setSortOption("main_start_year");
                    }}
                    hoverIndicator
                />
                <Button
                    plain
                    margin={{ horizontal: "10px" }}
                    label={
                        <SortOptionText
                            sort_option={sortOption}
                            sort_option_en={"start_year"}
                            sort_option_kr={"근무연도 시작순"}
                        />
                    }
                    onClick={() => {
                        sortOption !== "start_year" &&
                            changeSortOption({
                                option: "start_year",
                                person_psnorders: personPsnorders,
                            });
                        setSortOption("start_year");
                    }}
                    hoverIndicator
                />
            </Box>
        );
    };

    const SelectShowOption = () => {
        const ShowOptionText = (props: any) => {
            let show_option = props.show_option;
            let show_option_kr = props.show_option_kr;
            let show_option_en = props.show_option_en;
            return (
                <Text
                    size="12px"
                    weight={show_option === show_option_en ? "bold" : "normal"}
                >
                    {show_option_kr}
                </Text>
            );
        };
        return (
            <Box direction="row" align="center" justify="center" margin="10px">
                <Button
                    margin={{ horizontal: "5px" }}
                    plain
                    label={
                        <ShowOptionText
                            show_option={showOption}
                            show_option_en={"position"}
                            show_option_kr={"직책"}
                        />
                    }
                    onClick={() => {
                        showOption !== "position" && setShowOption("position");
                    }}
                    hoverIndicator
                />
                <Button
                    plain
                    margin={{ horizontal: "10px" }}
                    label={
                        <ShowOptionText
                            show_option={showOption}
                            show_option_en={"grade"}
                            show_option_kr={"관등"}
                        />
                    }
                    onClick={() => {
                        showOption !== "grade" && setShowOption("grade");
                    }}
                    hoverIndicator
                />
                <Button
                    plain
                    margin={{ horizontal: "10px" }}
                    label={
                        <ShowOptionText
                            show_option={showOption}
                            show_option_en={"merit"}
                            show_option_kr={"공훈"}
                        />
                    }
                    onClick={() => {
                        showOption !== "merit" && setShowOption("merit");
                    }}
                    hoverIndicator
                />
            </Box>
        );
    };
    const [showInstList, setShowInstList] = React.useState(false);
    const [selectedInsts, setSelectedInsts] = useRecoilState(
        selectedInstsAnalysisR
    );
    const [selectedInstsName, setSelectedInstsName] = useRecoilState(
        selectedInstsNameAnalysisR
    );

    React.useEffect(() => {
        const getInstByNameExact = async (inst_name: string) => {
            let res_inst = await useInst.getInstByNameExact(inst_name);
            if (res_inst) {
                return res_inst;
            } else {
                return null;
            }
        };
        if (
            readyOtherInstsInfo &&
            selectedInsts !== undefined &&
            selectedInsts.length > 0
        ) {
            // check if inst_id is null of selectedInsts
            // console.log("selectedInsts", selectedInsts)
            // find element without inst_id in selectedInsts
            let selectedInsts_without_inst_id = selectedInsts.filter(
                (p: any) => p.inst_id === null
            );
            // console.log(
            //     "selectedInsts_without_inst_id",
            //     selectedInsts_without_inst_id,
            //     selectedInsts
            // );

            if (selectedInsts_without_inst_id.length > 0) {
                // find inst_id of selectedInsts_without_inst_id
                const n_insts = selectedInsts_without_inst_id.length;
                let n_count = 0;
                selectedInsts_without_inst_id.forEach((x: any, index) => {
                    n_count += 1;
                    getInstByNameExact(x.primaryname).then((res_inst: any) => {
                        // console.log( "getInstByNameExact", res_inst, x.primaryname );
                        if (res_inst) {
                            let new_selectedInst = {
                                inst_id: res_inst.inst.inst_id,
                                primaryname: res_inst.inst.primaryname,
                                primaryname_ko: res_inst.inst.primaryname_ko,
                                inst_type: res_inst.inst.inst_type,
                                inst_category: res_inst.inst.inst_category,
                            };
                            // find in selectedInsts with inst_name
                            let index = selectedInsts.findIndex(
                                (p: any) => p.primaryname === x.primaryname
                            );
                            // replace selectedInsts[index] with new_selectedInst
                            let selectedInsts_copy = [...selectedInsts];
                            selectedInsts_copy[index] = new_selectedInst;
                            setSelectedInsts(selectedInsts_copy);
                            // replace otherInstsInfo with res_inst
                            let index2 = otherInstsInfo.findIndex(
                                (p: any) => p.inst.primaryname === x.primaryname
                            );
                            let otherInstsInfo_copy = [...otherInstsInfo];
                            otherInstsInfo_copy[index2] = res_inst;
                            setOtherInstsInfo(otherInstsInfo_copy);
                        }
                    });
                });
            }
        }
    }, [selectedInsts]);

    const gotoAnalysis = () => {
        let selected_ids_join = selectedInsts
            .map((p: any) => p.inst_id)
            .join("&");
        setShowInstList(false);
        setSelectedInsts([]);
        setSelectedInstsName([]);
        navigate("/redbook_analysis#" + selected_ids_join);
    };
    const ButtonText = (props: any) => {
        let name = props.name;
        return (
            <Text
                margin={{ horizontal: "10px" }}
                size="12px"
                weight={selectedInstsName.includes(name) ? "bold" : "normal"}
                color={
                    selectedInstsName.includes(name)
                        ? "rgb(200,100,100)"
                        : "black"
                }
            >
                - {name}
            </Text>
        );
    };
    const [selectInstType, setSelectInstType] = React.useState("검색기관");
    const InstTypeSelectButton = (props: any) => {
        let inst_type = props.inst_type;
        let n_inst = props.n_inst;
        return(
            <Button plain hoverIndicator
                label={
                    <Box pad='10px'>
                        <Text size="13px" weight={inst_type===selectInstType? "bold":"normal"}>{inst_type} [{n_inst}]</Text>
                    </Box>
                }
                onClick={()=> inst_type!==selectInstType && setSelectInstType(inst_type)}
            />)
    }

    return (
        <Box width="full" height="full">
            <PageMainCanvas>
                <Box
                    direction="row"
                    align="center"
                    width="full"
                    style={{ minHeight: "50px", maxHeight: "50px" }}
                >
                    <Button
                        plain
                        onClick={() => navigate("/redbooks")}
                        icon={<HiOutlineBuildingOffice size="30px" />}
                        label={
                            <Text margin={{ horizontal: "10px" }} size="20px">
                                직원록
                            </Text>
                        }
                        hoverIndicator
                    />
                    <Text size="20px">기관 관료 분석</Text>
                </Box>
                <Box>
                    <Box
                        pad="10px"
                        round="5px"
                        align="center"
                        justify="center"
                        style={{ minHeight: "70px" }}
                        flex={false}
                        direction="row"
                    >
                        <Text>
                            {mainInstsInfo.map((p: any) => (
                                <AInstButton
                                    key={p.inst.inst_id}
                                    name={p.inst.primaryname}
                                    name_ko={p.inst.primaryname_ko}
                                    id={p.inst.inst_id}
                                />
                            ))}
                        </Text>
                    </Box>
                </Box>
                <Box
                    justify="between"
                    direction="row"
                    style={{ minHeight: "30px", maxHeight: "30px" }}
                >
                    <Box margin={{ bottom: "5px" }}>
                        <Text size="15px">
                            검색기관 직원수 : {personPsnorders.length}
                        </Text>
                    </Box>
                    <Button
                        margin={{ horizontal: "10px" }}
                        plain
                        hoverIndicator
                        focusIndicator={false}
                        label={
                            <Box pad="5px">
                                <Text size="12px">
                                    목록 내 전체기관 보기 [
                                    {mainInstsInfo.length +
                                        relatedInstsInfo.length +
                                        otherInstsInfo.length}
                                    ]
                                </Text>
                            </Box>
                        }
                        onClick={() => setShowInstList(true)}
                    />
                </Box>
                <Box style={{ minHeight: "15px" }}>
                    <Text size="10px">
                        선택기관 : {selectedInstsName.length}
                    </Text>
                </Box>
                <Box
                    alignSelf="center"
                    style={{ minHeight: "10px" }}
                    width="full"
                    pad="10px"
                    flex={false}
                >
                    <Text size="12px">
                        {selectedInstsName.map((p: any, index) => (
                            <Text
                                size="12px"
                                margin={{ horizontal: "10px" }}
                                key={p}
                            >
                                <Text size="12px" color={colorPalette[index]}>
                                    *
                                </Text>
                                {p}
                            </Text>
                        ))}
                    </Text>
                </Box>
                {showInstList && (
                    <Layer
                        modal
                        position="top-right"
                        onEsc={() => setShowInstList(false)}
                        onClickOutside={() => setShowInstList(false)}
                    >
                        <Box
                            align="start"
                            justify="center"
                            pad="20px"
                            margin={{ top: "40px", bottom: "10px" }}
                            direction="column"
                            style={{ top: "50px" , minWidth:"350px"}}
                            height="full"
                            width="full"
                            flex={false}
                            background="rgba(240,240,240,0.8)"
                        >
                            <Box
                                align="start"
                                justify="start"
                                direction="column"
                                flex={false}
                                width="full"
                            >
                                <Text size="15px" weight="bold">
                                    선택기관 [{selectedInsts.length}]
                                </Text>
                                <Box alignSelf="end">
                                    <Button
                                        plain
                                        hoverIndicator
                                        focusIndicator={false}
                                        label={
                                            <Text size="10px">
                                                선택기관 비우기
                                            </Text>
                                        }
                                        onClick={() => {
                                            setSelectedInsts([]);
                                            setSelectedInstsName([]);
                                        }}
                                        disabled={selectedInsts.length === 0}
                                    />
                                </Box>
                                <Box
                                    border
                                    round="2px"
                                    width="full"
                                    style={{ minHeight: "50px" }}
                                    margin={{ vertical: "10px" }}
                                    pad="10px"
                                >
                                    {selectedInstsName.length > 0 &&
                                        selectedInsts.length > 0 &&
                                        selectedInstsName.map(
                                            (p: any, index) => (
                                                <Text
                                                    size="12px"
                                                    key={index.toString() + p}
                                                >
                                                    - {p}
                                                </Text>
                                            )
                                        )}
                                </Box>
                                <Box alignSelf="end">
                                    <Button
                                        plain
                                        hoverIndicator
                                        focusIndicator={false}
                                        label={
                                            <Text size="10px">
                                                선택기관으로 분석하기
                                            </Text>
                                        }
                                        icon={<LinkNext size="15px" />}
                                        onClick={() => gotoAnalysis()}
                                    />
                                </Box>
                            </Box>
                        </Box>
                        <Box
                            align="center"
                            justify="center"
                            direction="row"
                            width="full"
                            style={{ minHeight: "50px", maxHeight: "50px" }}
                        >
                            <InstTypeSelectButton
                                inst_type="검색기관"
                                n_inst={mainInstsInfo.length}
                            />
                            <InstTypeSelectButton
                                inst_type="관련기관"
                                n_inst={relatedInstsInfo.length}
                            />
                            <InstTypeSelectButton
                                inst_type="기타기관"
                                n_inst={otherInstsInfo.length}
                            />
                        </Box>
                        <Box
                            align="start"
                            justify="start"
                            direction="column"
                            margin={{ bottom: "10px" }}
                            overflow="auto"
                            width="full"
                            height='full'
                        >
                            {selectInstType === "검색기관" && (
                                <Box
                                    align="start"
                                    justify="center"
                                    pad="20px"
                                    direction="column"
                                    flex={false}
                                >
                                    {mainInstsInfo.map((p: any) => (
                                        <Button
                                            plain
                                            key={p.inst.primaryname}
                                            hoverIndicator
                                            label={
                                                <ButtonText
                                                    name={p.inst.primaryname}
                                                />
                                            }
                                            onClick={() => {
                                                if (
                                                    selectedInstsName.includes(
                                                        p.inst.primaryname
                                                    )
                                                ) {
                                                    setSelectedInstsName(
                                                        selectedInstsName.filter(
                                                            (q: any) =>
                                                                q !==
                                                                p.inst
                                                                    .primaryname
                                                        )
                                                    );
                                                    setSelectedInsts(
                                                        selectedInsts.filter(
                                                            (q: any) =>
                                                                q.primaryname !==
                                                                p.inst
                                                                    .primaryname
                                                        )
                                                    );
                                                } else {
                                                    let new_selectedInstsName =
                                                        [
                                                            ...selectedInstsName,
                                                            p.inst.primaryname,
                                                        ];
                                                    setSelectedInstsName(
                                                        new_selectedInstsName
                                                    );
                                                    let new_selectedInsts = [
                                                        ...selectedInsts,
                                                        p.inst,
                                                    ];
                                                    setSelectedInsts(
                                                        new_selectedInsts
                                                    );
                                                }
                                            }}
                                        />
                                    ))}
                                </Box>
                            )}
                            {selectInstType === "관련기관" && (
                                <Box
                                    align="start"
                                    justify="center"
                                    pad="20px"
                                    direction="column"
                                    flex={false}
                                >
                                    {relatedInstsInfo.map((p: any, index) => (
                                        <Button
                                            key={
                                                index.toString() +
                                                p.inst.primaryname
                                            }
                                            plain
                                            hoverIndicator
                                            label={
                                                <ButtonText
                                                    name={p.inst.primaryname}
                                                />
                                            }
                                            onClick={() => {
                                                if (
                                                    selectedInstsName.includes(
                                                        p.inst.primaryname
                                                    )
                                                ) {
                                                    setSelectedInstsName(
                                                        selectedInstsName.filter(
                                                            (q: any) =>
                                                                q !==
                                                                p.inst
                                                                    .primaryname
                                                        )
                                                    );
                                                    setSelectedInsts(
                                                        selectedInsts.filter(
                                                            (q: any) =>
                                                                q.primaryname !==
                                                                p.inst
                                                                    .primaryname
                                                        )
                                                    );
                                                } else {
                                                    setSelectedInstsName([
                                                        ...selectedInstsName,
                                                        p.inst.primaryname,
                                                    ]);
                                                    setSelectedInsts([
                                                        ...selectedInsts,
                                                        p.inst,
                                                    ]);
                                                }
                                            }}
                                        />
                                    ))}
                                </Box>
                            )}
                            {selectInstType === "기타기관" && (
                                <Box
                                    align="start"
                                    justify="center"
                                    pad="20px"
                                    direction="column"
                                    flex={false}
                                >
                                    {otherInstsInfo.map((p: any, index) => (
                                        <Button
                                            key={
                                                index.toString() +
                                                p.inst.primaryname
                                            }
                                            plain
                                            hoverIndicator
                                            label={
                                                <ButtonText
                                                    name={p.inst.primaryname}
                                                />
                                            }
                                            onClick={() => {
                                                if (
                                                    selectedInstsName.includes(
                                                        p.inst.primaryname
                                                    )
                                                ) {
                                                    setSelectedInstsName(
                                                        selectedInstsName.filter(
                                                            (q: any) =>
                                                                q !==
                                                                p.inst
                                                                    .primaryname
                                                        )
                                                    );
                                                    setSelectedInsts(
                                                        selectedInsts.filter(
                                                            (q: any) =>
                                                                q.primaryname !==
                                                                p.inst
                                                                    .primaryname
                                                        )
                                                    );
                                                } else {
                                                    setSelectedInstsName([
                                                        ...selectedInstsName,
                                                        p.inst.primaryname,
                                                    ]);
                                                    setSelectedInsts([
                                                        ...selectedInsts,
                                                        p.inst,
                                                    ]);
                                                }
                                            }}
                                        />
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Layer>
                )}

                <Box
                    align="center"
                    justify="between"
                    direction="row"
                    margin={{ top: "10px", horizontal: "10px" }}
                >
                    <SelectSortOption />
                    <SelectShowOption />
                </Box>
                {readyOtherInstsInfo &&
                    resultSorted &&
                    sortedPersonPsnorders !== undefined && (
                        <PageRedbookAnalysisTable
                            psnorders={sortedPersonPsnorders}
                            sort_option={sortOption}
                            sort_direction={sortDirection}
                            show_option={showOption}
                            main_insts_info={mainInstsInfo}
                            related_insts_info={relatedInstsInfo}
                            other_insts_info={otherInstsInfo}
                        />
                    )}
            </PageMainCanvas>
        </Box>
    );
};

export default PageRedbookAnalysis;
